import React, { useEffect, useState, useRef } from 'react';
import { NavLink, Link, useLocation } from 'react-router-dom';
import { Search, Menu, X, ChevronDown } from 'lucide-react';
import { Logo } from './Logo';
interface SubLink {
  name: string;
  path: string;
}
interface NavItem {
  name: string;
  path?: string;
  children?: SubLink[];
}
const navItems: NavItem[] = [{
  name: 'SERVICES',
  path: '/services'
}, {
  name: "MILANGELA'S STORY",
  path: '/milangela'
}, {
  name: 'SOUL GARDEN',
  children: [{
    name: 'Podcasts',
    path: '/podcasts'
  }, {
    name: 'Meditation',
    path: '/meditation'
  }, {
    name: 'Creative Corner',
    path: '/creative-corner'
  }, {
    name: 'Testimonies',
    path: '/testimonies'
  }]
}, {
  name: 'BLOG',
  path: '/blog'
}];
export function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  // Close menus on route change
  useEffect(() => {
    setMobileMenuOpen(false);
    setOpenDropdown(null);
  }, [location]);
  const isChildActive = (children?: SubLink[]) => children?.some(c => location.pathname === c.path) ?? false;
  return <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-cream/95 backdrop-blur-sm shadow-sm py-3' : 'bg-transparent py-5'}`} data-id="element-135">
      {/* Dark gradient scrim for readability on light hero images */}
      {!isScrolled && <div aria-hidden="true" className="absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-black/45 via-black/20 to-transparent pointer-events-none" data-id="element-136" />}
      <div className="relative max-w-7xl mx-auto px-6 flex items-center justify-between" data-id="element-137">
        {/* Logo */}
        <Link to="/" className="flex items-center z-50" data-id="element-138">
          <Logo variant={isScrolled ? 'dark' : 'white'} className={`transition-all duration-300 ${isScrolled ? 'h-10 md:h-12' : 'h-14 md:h-16'} w-auto`} data-id="element-139" />
        </Link>

        {/* Desktop Nav */}
        <nav ref={dropdownRef} className="hidden lg:flex items-center space-x-7" data-id="element-140">
          {navItems.map(item => {
          if (item.children) {
            const active = isChildActive(item.children);
            const isOpen = openDropdown === item.name;
            return <div key={item.name} className="relative" data-id="element-141">
                  <button onClick={() => setOpenDropdown(isOpen ? null : item.name)} className={`inline-flex items-center gap-1 text-sm tracking-widest font-medium transition-colors ${active ? isScrolled ? 'text-sage-700' : 'text-sage-100 [text-shadow:0_1px_2px_rgba(0,0,0,0.5)]' : isScrolled ? 'text-charcoal hover:text-sage-600' : 'text-white hover:text-white [text-shadow:0_1px_2px_rgba(0,0,0,0.5)]'}`} data-id="element-142">
                    {item.name}
                    <ChevronDown size={14} strokeWidth={1.5} className={`transition-transform ${isOpen ? 'rotate-180' : ''}`} data-id="element-143" />
                  </button>
                  {isOpen && <div className="absolute top-full left-1/2 -translate-x-1/2 mt-3 min-w-[220px] bg-white rounded-xl shadow-lg border border-cream-200 py-2 z-50" data-id="element-144">
                      {item.children.map(child => <NavLink key={child.path} to={child.path} className={({
                  isActive
                }) => `block px-5 py-2.5 text-sm transition-colors ${isActive ? 'text-sage-700 bg-cream-50' : 'text-charcoal hover:bg-cream-50'}`} data-id="element-145">
                          {child.name}
                        </NavLink>)}
                    </div>}
                </div>;
          }
          return <NavLink key={item.name} to={item.path!} className={({
            isActive
          }) => `text-sm tracking-widest font-medium transition-colors ${isActive ? isScrolled ? 'text-sage-700' : 'text-sage-100 [text-shadow:0_1px_2px_rgba(0,0,0,0.5)]' : isScrolled ? 'text-charcoal hover:text-sage-600' : 'text-white hover:text-white [text-shadow:0_1px_2px_rgba(0,0,0,0.5)]'}`} data-id="element-146">
                {item.name}
              </NavLink>;
        })}
        </nav>

        {/* Actions */}
        <div className="hidden lg:flex items-center space-x-6" data-id="element-147">
          <button aria-label="Search" className={isScrolled ? 'text-charcoal' : 'text-white'} data-id="element-148">
            <Search size={20} data-id="element-149" />
          </button>
          <Link to="/contact" className={`px-6 py-2 border text-sm tracking-widest transition-colors ${isScrolled ? 'border-charcoal text-charcoal hover:bg-charcoal hover:text-cream' : 'border-white text-white hover:bg-white hover:text-charcoal'}`} data-id="element-150">
            CONTACT
          </Link>
        </div>

        {/* Mobile Menu Toggle */}
        <button aria-label="Toggle menu" className="lg:hidden z-50" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} data-id="element-151">
          {mobileMenuOpen ? <X size={28} className="text-charcoal" data-id="element-152" /> : <Menu size={28} className={isScrolled ? 'text-charcoal' : 'text-white'} data-id="element-153" />}
        </button>

        {/* Mobile Nav */}
        {mobileMenuOpen && <div className="fixed inset-0 bg-cream z-40 overflow-y-auto pt-24 pb-12 px-8" data-id="element-154">
            <nav className="flex flex-col space-y-5 max-w-md mx-auto" data-id="element-155">
              {navItems.map(item => {
            if (item.children) {
              return <div key={item.name} data-id="element-156">
                      <p className="text-sm tracking-widest font-medium text-charcoal mb-3" data-id="element-157">
                        {item.name}
                      </p>
                      <div className="pl-4 flex flex-col space-y-3 border-l border-cream-200" data-id="element-158">
                        {item.children.map(child => <NavLink key={child.path} to={child.path} className={({
                    isActive
                  }) => `text-base ${isActive ? 'text-sage-700' : 'text-charcoal-50'}`} data-id="element-159">
                            {child.name}
                          </NavLink>)}
                      </div>
                    </div>;
            }
            return <NavLink key={item.name} to={item.path!} className={({
              isActive
            }) => `text-lg tracking-widest font-medium ${isActive ? 'text-sage-700' : 'text-charcoal'}`} data-id="element-160">
                    {item.name}
                  </NavLink>;
          })}
              <Link to="/contact" className="mt-4 inline-block w-fit px-8 py-3 border border-charcoal text-charcoal text-base tracking-widest" data-id="element-161">
                CONTACT
              </Link>
            </nav>
          </div>}
      </div>
    </header>;
}